import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';

// Default status colors
const DEFAULT_STATUS_COLORS = {
  'Empty in Customer': '#fce7f3',      // pink-100
  'Available for Pickup': '#e0f2fe',   // sky-100
  'Need Appt': '#fef3c7',              // yellow-100
  'On the way to Customer': '#ffedd5', // orange-100
  'Important Customer': '#fecaca',     // red-200
  'Delivered to Customer': '#bbf7d0',  // green-200
  'In Yard': '#ddd6fe',                // violet-200
  'Container Returned': '#e5e7eb',     // gray-200
  'On the way to Return': '#c7d2fe',   // indigo-200
  'Delivered to Exam Location': '#99f6e4', // teal-200
  'New Shipment': '#bfdbfe',           // blue-200
  'Empty Container in Yard': '#e2e8f0', // slate-200
  'On the way to Pickup': '#fde68a',   // amber-200
  'Shipment Closed': '#d4d4d8',        // zinc-300
  'Trouble': '#fecdd3'                 // rose-200
};

// Status key mapping for translations
const STATUS_TRANSLATION_KEYS = {
  'Empty in Customer': 'shipments.status.emptyInCustomer',
  'Available for Pickup': 'shipments.status.availableForPickup',
  'Need Appt': 'shipments.status.needAppt',
  'On the way to Customer': 'shipments.status.onTheWayToCustomer',
  'Important Customer': 'shipments.status.importantCustomer',
  'Delivered to Customer': 'shipments.status.deliveredToCustomer',
  'In Yard': 'shipments.status.inYard',
  'Container Returned': 'shipments.status.containerReturned',
  'On the way to Return': 'shipments.status.onTheWayToReturn',
  'Delivered to Exam Location': 'shipments.status.deliveredToExamLocation',
  'New Shipment': 'shipments.status.newShipment',
  'Empty Container in Yard': 'shipments.status.emptyContainerInYard',
  'On the way to Pickup': 'shipments.status.onTheWayToPickup',
  'Shipment Closed': 'shipments.status.shipmentClosed',
  'Trouble': 'shipments.status.trouble'
};

export const StatusColorSettings = ({ 
  open, 
  onOpenChange, 
  currentColors,
  onSave,
  api,
  preferencesKey = 'status_colors'
}) => {
  const { t } = useTranslation();
  const [colors, setColors] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (open) {
      // Build colors from the canonical DEFAULT list, applying any saved overrides
      const canonical = { ...DEFAULT_STATUS_COLORS };
      if (currentColors) {
        Object.keys(canonical).forEach(status => {
          if (currentColors[status]) {
            canonical[status] = currentColors[status];
          }
        });
      }
      setColors(canonical);
      setHasChanges(false);
    }
  }, [open, currentColors]);

  const handleColorChange = (status, color) => {
    setColors(prev => ({ ...prev, [status]: color }));
    setHasChanges(true);
  };

  const handleReset = () => {
    setColors({ ...DEFAULT_STATUS_COLORS });
    setHasChanges(true);
  };

  // Helper to get translated status label
  const getStatusLabel = (status) => {
    const key = STATUS_TRANSLATION_KEYS[status];
    return key ? t(key) : status;
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Only persist colors that differ from defaults
      const overrides = {};
      Object.entries(colors).forEach(([status, color]) => {
        if (DEFAULT_STATUS_COLORS[status] && color !== DEFAULT_STATUS_COLORS[status]) {
          overrides[status] = color;
        }
      });
      await api.put('/user/preferences', {
        [preferencesKey]: overrides
      });
      onSave(colors);
      toast.success('Status colors saved');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to save colors');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 sm:max-w-[550px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">{t('dashboard.statusColors')}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {t('settings.languageDesc', { defaultValue: 'Choose colors for each shipment status. Changes will apply to the dashboard row backgrounds.' })}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 gap-3 py-4">
          {Object.entries(colors).map(([status, color]) => (
            <div 
              key={status} 
              className="flex items-center justify-between p-2 rounded-lg"
              style={{ backgroundColor: color + '40' }}
            >
              <Label className="text-sm text-white font-medium">{getStatusLabel(status)}</Label>
              <div className="flex items-center gap-2">
                <div 
                  className="w-8 h-8 rounded border border-slate-600"
                  style={{ backgroundColor: color }}
                />
                <input
                  type="color"
                  value={color}
                  onChange={(e) => handleColorChange(status, e.target.value)}
                  className="w-10 h-8 rounded cursor-pointer border-0 bg-transparent"
                  data-testid={`color-${status.replace(/\s+/g, '-').toLowerCase()}`}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between pt-4 border-t border-slate-800">
          <Button
            variant="outline"
            onClick={handleReset}
            className="border-slate-700 text-slate-300"
            data-testid="reset-colors-btn"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || loading}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="save-colors-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Colors
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StatusColorSettings;
